// V1.9 — lightweight XP / level system. Fills the 5 / 15 / 40 cosmetic
// tier gap with per-match feedback so a player who's lost five times in
// a row still sees the bar move and a level pop. Stored under
// `signalclash.profile.xp` so the existing CrazyGames Data Module
// hydrate/install layer (CrazyGamesSdk.ts) syncs it across devices for
// free — no SDK changes required.
//
// Award shape per match (reflected in `awardMatchXp`):
//   win                  100
//   loss (completed)      30
//   per relay held end    +5
//   per squad built       +1
//   per viewer kill       +2
//
// Levels are computed lazily from XP via `levelFromXp`. The curve is
// quadratic — each level costs 100 more XP than the previous, so the
// pace stays meaningful for newcomers (level 2 at 100 XP, level 3 at
// 300, level 4 at 600) without trivializing the lifetime ladder for
// returning players.

import type { MatchState, PlayerId } from '@signalclash/shared';

import { applyMatchXpModifier } from './WeeklyModifier.js';

const XP_KEY = 'signalclash.profile.xp';

export const XP_FOR_WIN = 100;
export const XP_FOR_LOSS = 30;
export const XP_PER_RELAY_HELD = 5;
export const XP_PER_SQUAD_BUILT = 1;
export const XP_PER_KILL = 2;

export interface XpState {
  xp: number;
  level: number;
  xpIntoLevel: number;
  xpToNextLevel: number;
}

export interface XpAward {
  before: XpState;
  after: XpState;
  delta: number;
  leveledUp: boolean;
  breakdown: XpBreakdown;
}

export interface XpBreakdown {
  outcome: number;
  relays: number;
  squads: number;
  kills: number;
}

// Quadratic curve: total XP needed to *reach* level N (1-indexed, where
// level 1 starts at xp=0). Lvl 2 = 100, Lvl 3 = 300, Lvl 4 = 600, etc.
function xpRequiredForLevel(level: number): number {
  if (level <= 1) return 0;
  return 50 * (level - 1) * level;
}

export function levelFromXp(xp: number): number {
  if (xp <= 0) return 1;
  let lvl = 1;
  while (xpRequiredForLevel(lvl + 1) <= xp) lvl += 1;
  return lvl;
}

export function deriveXpState(xp: number): XpState {
  const safeXp = Math.max(0, Math.floor(xp));
  const level = levelFromXp(safeXp);
  const floor = xpRequiredForLevel(level);
  const ceil = xpRequiredForLevel(level + 1);
  return {
    xp: safeXp,
    level,
    xpIntoLevel: safeXp - floor,
    xpToNextLevel: ceil - floor,
  };
}

export function loadXpState(): XpState {
  return deriveXpState(readXp());
}

export function awardMatchXp(
  finalState: MatchState | null,
  viewerId: PlayerId,
  viewerKills: number,
  won: boolean,
): XpAward {
  const beforeXp = readXp();
  const before = deriveXpState(beforeXp);
  const breakdown = computeBreakdown(finalState, viewerId, viewerKills, won);
  const rawDelta = breakdown.outcome + breakdown.relays + breakdown.squads + breakdown.kills;
  // V2 Tempo — apply this week's modifier (Double XP weeks etc.) to the
  // total. Breakdown chips still show the BASE numbers so the player
  // can see where each chunk came from; the headline delta reflects
  // the multiplied total.
  const delta = applyMatchXpModifier(rawDelta);
  const afterXp = beforeXp + delta;
  writeXp(afterXp);
  const after = deriveXpState(afterXp);
  return {
    before,
    after,
    delta,
    leveledUp: after.level > before.level,
    breakdown,
  };
}

function computeBreakdown(
  finalState: MatchState | null,
  viewerId: PlayerId,
  viewerKills: number,
  won: boolean,
): XpBreakdown {
  const outcome = won ? XP_FOR_WIN : XP_FOR_LOSS;
  let relays = 0;
  let squads = 0;
  if (finalState !== null) {
    for (const node of Object.values(finalState.controlNodes)) {
      if (node.ownerId === viewerId) relays += 1;
    }
    squads = finalState.stats.squadsBuilt[viewerId] ?? 0;
  }
  return {
    outcome,
    relays: relays * XP_PER_RELAY_HELD,
    squads: squads * XP_PER_SQUAD_BUILT,
    kills: Math.max(0, viewerKills) * XP_PER_KILL,
  };
}

function readXp(): number {
  if (typeof localStorage === 'undefined') return 0;
  try {
    const raw = localStorage.getItem(XP_KEY);
    if (raw === null) return 0;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

function writeXp(value: number): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(XP_KEY, String(Math.max(0, Math.floor(value))));
  } catch {
    // Quota / privacy mode — non-fatal, XP stays session-local.
  }
}
